// L'EFFACEUR — Panel admin Audit Billing
// Liste les missions audit en attente de facturation, permet :
//   1. Saisir les économies réelles annuelles
//   2. Émettre la facture Stripe (commission 20%)
// Bloque le revenue Pack Audit tant que pas codé.

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { DollarSign, FileText, CheckCircle, Clock, RefreshCw, AlertCircle, ExternalLink } from 'lucide-react';
import { API } from '../../App';
import { Button } from '../ui/button';
import EmptyState from '../ui/empty-state';
import { SkeletonCard } from '../ui/loading-skeleton';

const AuditBillingPanel = () => {
  const [pendingMissions, setPendingMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMission, setSelectedMission] = useState(null);
  const [savingsInput, setSavingsInput] = useState('');
  const [notesInput, setNotesInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [issuing, setIssuing] = useState(false);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/audit-billing/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendingMissions(res.data.missions || []);
    } catch (e) {
      toast.error('Erreur chargement missions audit');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const validateSavings = async (missionId) => {
    const savings = parseFloat(savingsInput);
    if (isNaN(savings) || savings < 0) {
      toast.error('Montant invalide');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/admin/audit-billing/${missionId}/validate-savings`,
        { annual_savings_eur: savings, notes: notesInput },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Économies validées : ${savings}€/an, commission ${res.data.commission_eur}€`);
      // Mettre à jour la mission selectedMission
      const updated = await refreshMissionStatus(missionId);
      setSelectedMission(updated);
      fetchPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur validation');
    } finally {
      setSubmitting(false);
    }
  };

  const issueInvoice = async (missionId) => {
    if (!window.confirm("Émettre la facture Stripe ? Le client recevra un email de paiement.")) return;
    setIssuing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/admin/audit-billing/${missionId}/issue-invoice`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Facture émise : ${res.data.amount_eur}€`);
      setSelectedMission(null);
      fetchPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur émission facture');
    } finally {
      setIssuing(false);
    }
  };

  const refreshMissionStatus = async (missionId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/audit-billing/${missionId}/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    } catch {
      return null;
    }
  };

  const openMission = async (mission) => {
    const status = await refreshMissionStatus(mission.mission_id);
    setSelectedMission(status || mission);
    // Pré-remplir avec pre-calc si dispo
    const billing = (status || mission).audit_billing || mission.audit_billing || {};
    if (billing.pre_calculated_savings_eur) {
      setSavingsInput(String(billing.pre_calculated_savings_eur));
    } else if (billing.validated_savings_eur) {
      setSavingsInput(String(billing.validated_savings_eur));
    } else {
      setSavingsInput('');
    }
    setNotesInput(billing.validation_notes || '');
  };

  const closeMission = () => {
    setSelectedMission(null);
    setSavingsInput('');
    setNotesInput('');
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-1 h-8 bg-success rounded-full" style={{ boxShadow: '0 0 10px rgba(0,255,148,0.8)' }} />
          <h2 className="font-orbitron text-xl font-black text-white">Audit Billing</h2>
          <span className="font-mono text-xs text-success/60">// FACTURATION_COMMISSION</span>
        </div>
        <Button onClick={fetchPending} variant="outline" className="text-xs">
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </div>

      <div className="glass-card border border-success/20 p-4">
        <p className="text-sm text-gray-300 leading-relaxed">
          Missions Audit terminées en attente de facturation. <strong>Étape 1</strong> : valider le montant des économies réelles. <strong>Étape 2</strong> : émettre la facture Stripe (commission 20% auto-calculée). Le client reçoit un email de paiement.
        </p>
      </div>

      {pendingMissions.length === 0 ? (
        <EmptyState
          icon={DollarSign}
          title="Aucune mission audit à facturer"
          description="Les missions Pack Audit terminées apparaîtront ici une fois que le client aura uploadé son relevé de suivi (ou que tu peux saisir manuellement)."
          size="md"
        />
      ) : (
        <div className="space-y-3">
          {pendingMissions.map((mission) => {
            const billing = mission.audit_billing || {};
            const hasPreCalc = !!billing.pre_calculated_savings_eur;
            return (
              <motion.div
                key={mission.mission_id}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card border border-gray-800 hover:border-success/30 transition-colors p-4 cursor-pointer"
                onClick={() => openMission(mission)}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono text-cyan-400 text-sm">{mission.mission_id}</span>
                      {hasPreCalc && (
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-success/20 text-success border border-success/40">
                          PRÉ-CALCULÉ {Math.round(billing.pre_calc_confidence * 100)}%
                        </span>
                      )}
                      {billing.validated_savings_eur && (
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 border border-cyan-500/40">
                          ÉCONOMIES VALIDÉES
                        </span>
                      )}
                    </div>
                    <div className="text-white font-bold">{mission.user_name || mission.user_email}</div>
                    <div className="text-xs text-gray-400">{mission.user_email}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Estimées : {mission.estimated_annual_savings || 0}€/an · Créée {mission.created_at?.slice(0, 10)}
                    </div>
                  </div>
                  <div className="text-right">
                    {billing.validated_savings_eur ? (
                      <>
                        <div className="text-xl font-black text-success">{billing.commission_eur?.toFixed(2)}€</div>
                        <div className="text-xs text-gray-500">commission</div>
                      </>
                    ) : (
                      <Clock className="w-5 h-5 text-orange-400" />
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Modal de validation */}
      {selectedMission && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={closeMission}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card border border-success/40 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-orbitron text-lg font-bold text-white">Valider la facturation</h3>
              <button onClick={closeMission} className="text-gray-500 hover:text-white text-2xl leading-none">×</button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-gray-500 font-mono uppercase mb-1">Mission</div>
                  <div className="text-cyan-400 font-mono">{selectedMission.mission_id}</div>
                </div>
                <div>
                  <div className="text-gray-500 font-mono uppercase mb-1">Client</div>
                  <div className="text-white">{selectedMission.user_email}</div>
                </div>
                <div>
                  <div className="text-gray-500 font-mono uppercase mb-1">Économies estimées (initiales)</div>
                  <div className="text-white">{selectedMission.estimated_savings_annual || 0}€/an</div>
                </div>
                <div>
                  <div className="text-gray-500 font-mono uppercase mb-1">Statut facturation</div>
                  <div className="text-white">{selectedMission.invoice_status || 'En attente'}</div>
                </div>
              </div>

              {selectedMission.audit_billing?.pre_calc_details && (
                <div className="bg-success/10 border border-success/30 rounded p-3 text-xs">
                  <div className="text-success font-bold mb-2">Pré-calcul auto (Audit Validator)</div>
                  <div className="text-gray-300 mb-1">
                    {selectedMission.audit_billing.pre_calc_details.confirmed_disappeared_count} sur {selectedMission.audit_billing.pre_calc_details.selected_count} résiliations confirmées
                  </div>
                  <div className="text-gray-400">
                    Confiance : {Math.round((selectedMission.audit_billing.pre_calc_confidence || 0) * 100)}%
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-mono text-gray-400 uppercase mb-2">Économies réelles annuelles (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={savingsInput}
                  onChange={(e) => setSavingsInput(e.target.value)}
                  placeholder="ex : 487.50"
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white focus:border-success focus:outline-none"
                />
                {savingsInput && !isNaN(parseFloat(savingsInput)) && (
                  <p className="text-xs text-success mt-1">
                    Commission 20% : <strong>{(parseFloat(savingsInput) * 0.20).toFixed(2)}€</strong>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-mono text-gray-400 uppercase mb-2">Notes (optionnel)</label>
                <textarea
                  value={notesInput}
                  onChange={(e) => setNotesInput(e.target.value)}
                  placeholder="Ex : 4 abonnements résiliés, 2 négociés à un tarif inférieur..."
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm focus:border-success focus:outline-none"
                />
              </div>

              <div className="flex gap-2 flex-wrap pt-2">
                <Button
                  onClick={() => validateSavings(selectedMission.mission_id)}
                  disabled={submitting || !savingsInput}
                  className="bg-success text-black hover:bg-green-400 font-bold"
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  {submitting ? 'Validation...' : 'Valider les économies'}
                </Button>
                {selectedMission.audit_billing?.validated_savings_eur && !selectedMission.invoice_id && (
                  <Button
                    onClick={() => issueInvoice(selectedMission.mission_id)}
                    disabled={issuing}
                    className="bg-cyan-400 text-black hover:bg-cyan-300 font-bold"
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    {issuing ? 'Émission...' : 'Émettre facture Stripe'}
                  </Button>
                )}
                {selectedMission.invoice_url && (
                  <a
                    href={selectedMission.invoice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-4 py-2 border border-gray-700 rounded text-sm text-gray-300 hover:border-cyan-400 hover:text-cyan-400"
                  >
                    <ExternalLink className="w-4 h-4" /> Voir facture
                  </a>
                )}
              </div>

              {selectedMission.invoice_id && (
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded p-3 text-xs">
                  <div className="flex items-center gap-2 text-cyan-400 mb-1">
                    <CheckCircle className="w-4 h-4" /> Facture émise
                  </div>
                  <div className="text-gray-300">
                    ID : <code className="text-cyan-400">{selectedMission.invoice_id}</code>
                  </div>
                  {selectedMission.paid_at && (
                    <div className="text-success mt-1">Payée le {selectedMission.paid_at.slice(0, 10)}</div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AuditBillingPanel;
